import React, { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  Lock,
  ExternalLink,
  Clock,
  Globe,
  BarChart3,
  FileText,
  ChevronDown,
  ChevronRight,
  List,
  Palette,
  Keyboard,
  Image as ImageIcon,
  FormInput,
  Zap,
  Heart,
  ShieldCheck
} from 'lucide-react';
import { ScanResult } from '../types/index';
import { ScoreChart } from './ScoreChart';
import { MetricsDashboard } from './MetricsDashboard';
import { Tabs, Tab } from './Tabs';
import { ExportButtons } from './ExportButtons';

interface ScanResultsProps {
  result: ScanResult;
  onNewScan: () => void;
}

export const ScanResults: React.FC<ScanResultsProps> = ({ result, onNewScan }) => {
  const [activeTab, setActiveTab] = useState<string>('all');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['summary']));

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  // Define tabs based on available issues
  const tabs: Tab[] = [
    {
      id: 'all',
      label: 'Overview',
      icon: <List className="w-4 h-4" />,
      count: result.summary.totalIssues
    },
    {
      id: 'critical',
      label: 'Critical',
      icon: <XCircle className="w-4 h-4 text-red-500" />,
      count: result.summary.criticalIssues
    },
    ...(result.issues.axeIssues.length > 0 ? [{
      id: 'violations',
      label: 'Violations',
      icon: <AlertTriangle className="w-4 h-4" />,
      count: result.issues.axeIssues.length
    }] : []),
    ...(result.issues.colorContrastIssues.length > 0 ? [{
      id: 'contrast',
      label: 'Contrast',
      icon: <Palette className="w-4 h-4" />,
      count: result.issues.colorContrastIssues.length
    }] : []),
    ...(result.issues.keyboardNavigationIssues.length > 0 ? [{
      id: 'keyboard',
      label: 'Keyboard',
      icon: <Keyboard className="w-4 h-4" />,
      count: result.issues.keyboardNavigationIssues.length
    }] : []),
    ...(result.issues.imageAccessibilityIssues.length > 0 ? [{
      id: 'images',
      label: 'Images',
      icon: <ImageIcon className="w-4 h-4" />,
      count: result.issues.imageAccessibilityIssues.length
    }] : []),
    ...(result.issues.formAccessibilityIssues.length > 0 ? [{
      id: 'forms',
      label: 'Forms',
      icon: <FormInput className="w-4 h-4" />,
      count: result.issues.formAccessibilityIssues.length
    }] : []),
    {
      id: 'review',
      label: 'Needs Review',
      icon: <ShieldCheck className="w-4 h-4 text-amber-500" />,
      count: result.issues.axeIssues.filter(i => i.needsReview).length
    }
  ];

  const getComplianceColor = (level: string) => {
    switch (level) {
      case 'AAA': return 'text-green-700 bg-green-50 border-green-200';
      case 'AA': return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      case 'A': return 'text-orange-700 bg-orange-50 border-orange-200';
      default: return 'text-red-700 bg-red-50 border-red-200';
    }
  };

  const getImpactBadge = (impact: string) => {
    switch (impact) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'serious': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'moderate': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'minor': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDuration = (ms: number) => {
    return `${(ms / 1000).toFixed(1)}s`;
  };

  // Modern Section Component
  const Section: React.FC<{
    title: string;
    id: string;
    icon: React.ReactNode;
    children: React.ReactNode;
    count?: number;
    defaultOpen?: boolean;
  }> = ({ title, id, icon, children, count }) => {
    const isExpanded = expandedSections.has(id);

    return (
      <div className="glass rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl border border-white/50">
        <button
          onClick={() => toggleSection(id)}
          className={`w-full flex items-center justify-between px-6 py-4 text-left transition-colors ${isExpanded ? 'bg-white/50' : 'hover:bg-white/30'}`}
          aria-expanded={isExpanded}
          aria-controls={`${id}-content`}
        >
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isExpanded ? 'bg-primary-50 text-primary-600' : 'bg-gray-50 text-gray-500'}`}>
              {icon}
            </div>
            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
            {count !== undefined && (
              <span className="ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                {count}
              </span>
            )}
          </div>
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {isExpanded && (
          <div id={`${id}-content`} className="p-6 animate-fade-in">
            {children}
          </div>
        )}
      </div>
    );
  };

  // Determine if specific content is locked
  const isLocked = (level?: string, type?: string) => {
    if (result.isPaid) return false;
    if (level === 'AA' || level === 'AAA') return true;
    if (type === 'images') return true;
    return false;
  };

  const LockedOverlay = ({ type }: { type: string }) => (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-6 text-center bg-white/40 backdrop-blur-md rounded-xl border border-white/50 animate-fade-in shadow-xl">
      <div className="p-3 bg-amber-100 rounded-full mb-4 shadow-inner">
        <Lock className="w-8 h-8 text-amber-600" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">Premium Data Locked</h3>
      <p className="text-gray-600 text-sm max-w-xs mb-6">
        Detailed analysis for {type} is a professional feature. Unlock all premium data and download your PDF report for just $5.
      </p>
      <button
        onClick={() => {
          // Trigger checkout session
          const btn = document.querySelector('[data-checkout-btn]') as HTMLButtonElement;
          if (btn) btn.click();
        }}
        className="btn-primary flex items-center bg-amber-600 hover:bg-amber-700 border-amber-500 shadow-lg shadow-amber-500/20"
      >
        <Zap className="w-4 h-4 mr-2" />
        Unlock Professional Report
      </button>
    </div>
  );

  // Filter issues based on active tab
  const getFilteredIssues = () => {
    // ... same as before but maybe handle locked states
    const issues = {
      axeIssues: result.issues.axeIssues,
      colorContrastIssues: result.issues.colorContrastIssues,
      keyboardNavigationIssues: result.issues.keyboardNavigationIssues,
      imageAccessibilityIssues: result.issues.imageAccessibilityIssues,
      formAccessibilityIssues: result.issues.formAccessibilityIssues,
      structuralIssues: result.issues.structuralIssues
    };

    switch (activeTab) {
      case 'all': return issues;
      case 'critical':
        return {
          ...issues,
          axeIssues: issues.axeIssues.filter(issue => issue.impact === 'critical'),
          colorContrastIssues: [],
          keyboardNavigationIssues: issues.keyboardNavigationIssues.filter(issue => issue.severity === 'high'),
          imageAccessibilityIssues: [],
          formAccessibilityIssues: issues.formAccessibilityIssues.filter(issue => issue.severity === 'high'),
          structuralIssues: issues.structuralIssues.filter(issue => issue.severity === 'high')
        };
      case 'review':
        return {
          ...issues,
          axeIssues: issues.axeIssues.filter(issue => issue.needsReview),
          colorContrastIssues: [],
          keyboardNavigationIssues: [],
          imageAccessibilityIssues: [],
          formAccessibilityIssues: [],
          structuralIssues: []
        };
      default: return issues;
    }
  };

  const filteredIssues = getFilteredIssues();

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    if (tabId !== 'all') {
      const sectionMap: Record<string, string> = {
        'violations': 'axe-issues',
        'contrast': 'contrast-issues',
        'keyboard': 'keyboard-issues',
        'images': 'image-issues',
        'forms': 'form-issues',
        'review': 'review-issues'
      };
      const elId = sectionMap[tabId];
      if (elId) {
        setExpandedSections(prev => new Set(prev).add(elId));
        setTimeout(() => document.getElementById(elId)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  };

  // Helper to render issue card with potential blur
  const renderIssueCard = (issue: any, index: number, type: 'axe' | 'contrast' | 'keyboard' | 'image' | 'form' | 'structural') => {
    let level = issue.wcagLevel;
    let isFeatureLocked = isLocked(level, type === 'image' ? 'images' : undefined);

    if (type === 'axe') {
      return (
        <div key={index} className={`relative bg-white rounded-xl border border-gray-200 shadow-sm transition-all ${isFeatureLocked ? 'overflow-hidden min-h-[150px]' : 'p-5 hover:shadow-md'}`}>
          <div className={`${isFeatureLocked ? 'blur-sm grayscale opacity-30 p-5 pointer-events-none select-none' : ''}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className={`px-2.5 py-0.5 rounded text-xs font-bold uppercase tracking-wide border ${getImpactBadge(issue.impact)}`}>{issue.impact}</span>
                <span className="text-xs font-mono text-gray-400">WCAG {issue.wcagLevel}</span>
              </div>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">{issue.help}</h4>
            <p className="text-gray-600 mb-4">{issue.description}</p>

            {issue.nodes.map((node: any, ni: number) => (
              <div key={ni} className="space-y-3 mb-4">
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-bold text-gray-400 uppercase">HTML Snippet</span>
                  <div className="bg-gray-900 rounded-lg p-3 font-mono text-xs text-gray-300 overflow-x-auto border border-gray-800">
                    {node.html}
                  </div>
                </div>
                {node.selector && (
                  <div className="flex flex-col gap-2">
                    <span className="text-xs font-bold text-gray-400 uppercase">CSS Selector</span>
                    <div className="bg-gray-100 rounded-lg p-2 font-mono text-[10px] text-gray-600 border border-gray-200 break-all">
                      {node.selector}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {issue.friendlyMessage && (
              <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-4 mb-4">
                <h5 className="text-sm font-bold text-blue-900 flex items-center mb-1"><Info className="w-4 h-4 mr-2" /> Recommendation</h5>
                <p className="text-sm text-blue-800">{issue.friendlyMessage.fix}</p>
              </div>
            )}
          </div>
          {isFeatureLocked && <LockedOverlay type={`WCAG Level ${level} Issues`} />}
        </div>
      );
    }

    if (type === 'contrast') {
      return (
        <div key={index} className={`relative bg-white border border-gray-200 shadow-sm transition-all rounded-xl ${isFeatureLocked ? 'overflow-hidden min-h-[120px]' : 'p-4 hover:shadow-md'}`}>
          <div className={`${isFeatureLocked ? 'blur-md grayscale opacity-20 p-4 pointer-events-none select-none' : ''}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex -space-x-2">
                <div style={{ backgroundColor: issue.foregroundColor }} className="w-6 h-6 rounded-full border border-gray-200 shadow-sm" title="Text Color"></div>
                <div style={{ backgroundColor: issue.backgroundColor }} className="w-6 h-6 rounded-full border border-gray-200 shadow-sm" title="Background"></div>
              </div>
              <span className="font-mono text-xs font-bold bg-orange-100 text-orange-800 px-2 py-0.5 rounded">
                WCAG {issue.wcagLevel} • {issue.ratio.toFixed(2)}:1
              </span>
            </div>
            <p className="text-sm font-medium text-gray-900 truncate">"{issue.text}"</p>
            <p className="text-xs text-gray-500 mt-1 truncate">{issue.element}</p>
          </div>
          {isFeatureLocked && <LockedOverlay type={`Contrast Level ${level}`} />}
        </div>
      );
    }

    if (type === 'image') {
      return (
        <div key={index} className={`relative bg-white rounded-xl border border-gray-200 shadow-sm transition-all ${isFeatureLocked ? 'overflow-hidden min-h-[150px]' : 'p-4'}`}>
          <div className={`${isFeatureLocked ? 'blur-md grayscale opacity-20 p-4 pointer-events-none select-none' : ''}`}>
            <h4 className="font-bold text-gray-900 mb-1">{issue.issue}</h4>
            <p className="text-sm text-gray-600 mb-2">{issue.recommendation}</p>
            <div className="text-xs font-mono bg-gray-50 p-2 rounded text-gray-500">{issue.element}</div>
          </div>
          {isFeatureLocked && <LockedOverlay type="Image Accessibility Analysis" />}
        </div>
      );
    }

    // Default simple render for others
    return (
      <div key={index} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-bold text-gray-900">{(issue as any).issue}</h4>
          <span className={`px-2 py-0.5 rounded text-xs font-bold ${getSeverityBadge((issue as any).severity)}`}>{(issue as any).severity}</span>
        </div>
        <p className="text-sm text-gray-600 mb-2">{(issue as any).recommendation}</p>
        <div className="text-xs font-mono bg-gray-50 p-2 rounded text-gray-500">{(issue as any).element}</div>
      </div>
    );
  };

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8 animate-fade-in pb-20">
      {/* Header with Glass Effect */}
      <div className="glass rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative overflow-hidden">
        <div className="relative z-10 w-full">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-primary-100 text-primary-600 rounded-lg">
                <Zap className="h-5 w-5" />
              </div>
              <span className="text-sm font-semibold text-primary-700 tracking-wide uppercase">Audit Report</span>
            </div>
            <div className="text-sm text-gray-500 font-medium font-mono">
              {new Date(result.timestamp).toLocaleDateString()} • {new Date(result.timestamp).toLocaleTimeString()}
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4 break-all">
            {result.pageInfo.title || "Untitled Page"}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm">
            <a href={result.url} target="_blank" rel="noopener noreferrer" className="flex items-center px-3 py-1.5 bg-white/50 rounded-full border border-gray-200 text-gray-700 hover:bg-white hover:border-gray-300 transition-all">
              <Globe className="w-4 h-4 mr-2" />
              {result.url}
              <ExternalLink className="w-3 h-3 ml-2 text-gray-400" />
            </a>
            <div className="flex items-center px-3 py-1.5 bg-white/50 rounded-full border border-gray-200 text-gray-600">
              <Clock className="w-4 h-4 mr-2" />
              {formatDuration(result.scanDuration)}
            </div>
          </div>
        </div>

        <div className="relative z-10 flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <div data-checkout-btn>
            <ExportButtons result={result} summary={result.summary} plan="pro" />
          </div>
          <button onClick={onNewScan} className="btn-primary shadow-lg shadow-primary-500/30">
            New Scan
          </button>
        </div>

        {/* Decorative background blur */}
        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-gradient-to-br from-primary-100 to-purple-100 rounded-full blur-3xl opacity-50"></div>
      </div>

      {/* Metrics Section */}
      <MetricsDashboard summary={result.summary} scanDuration={result.scanDuration} url={result.url} />

      {/* Compliance Summary - Now Full Width */}
      <Section title="Compliance Summary" id="summary" icon={<BarChart3 className="w-5 h-5" />}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          <div className="lg:col-span-5 flex flex-col gap-4">
            <div className="bg-gray-50/50 rounded-xl p-6 border border-gray-100 flex-1 flex flex-col justify-center">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">WCAG Level</h3>
              <div className="flex items-center justify-between">
                <span className="text-gray-700 font-bold text-lg">Compliance Tier</span>
                <span className={`px-4 py-1.5 rounded-full text-sm font-black border shadow-sm ${getComplianceColor(result.summary.wcagLevel)}`}>
                  {result.summary.wcagLevel}
                </span>
              </div>
            </div>

            <div className="bg-gray-50/50 rounded-xl p-6 border border-gray-100 flex-1 flex flex-col justify-center">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">Section 508</h3>
              <div className="flex items-center justify-between">
                <span className="text-gray-700 font-bold text-lg">Federal Standard</span>
                <div className={`flex items-center font-black text-lg ${result.summary.section508Compliant ? 'text-green-600' : 'text-red-600'}`}>
                  {result.summary.section508Compliant ? <CheckCircle className="w-6 h-6 mr-2" /> : <XCircle className="w-6 h-6 mr-2" />}
                  {result.summary.section508Compliant ? 'Compliant' : 'Failed'}
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <ScoreChart scores={result.score} />
          </div>
        </div>
      </Section>

      {/* Issues Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Col: Issue Details */}
        <div className="lg:col-span-2 space-y-8">
          {/* Filter Tabs */}
          <div className="sticky top-20 z-40 bg-gray-50/90 backdrop-blur pt-4 pb-2">
            <Tabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
          </div>

          {/* Issue Sections */}
          <div className="space-y-6">
            {filteredIssues.axeIssues.length > 0 && (
              <Section title="Violations" id="axe-issues" icon={<AlertTriangle className="w-5 h-5 text-red-500" />} count={filteredIssues.axeIssues.length}>
                <div className="space-y-4">
                  {filteredIssues.axeIssues.map((issue, i) => renderIssueCard(issue, i, 'axe'))}
                </div>
              </Section>
            )}

            {filteredIssues.colorContrastIssues.length > 0 && (
              <Section title="Contrast" id="contrast" icon={<Palette className="w-5 h-5 text-orange-500" />} count={filteredIssues.colorContrastIssues.length}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredIssues.colorContrastIssues.map((issue, i) => renderIssueCard(issue, i, 'contrast'))}
                </div>
              </Section>
            )}

            {filteredIssues.keyboardNavigationIssues.length > 0 && (
              <Section title="Keyboard Navigation" id="keyboard-issues" icon={<Keyboard className="w-5 h-5 text-purple-500" />} count={filteredIssues.keyboardNavigationIssues.length}>
                <div className="space-y-4">
                  {filteredIssues.keyboardNavigationIssues.map((issue, i) => renderIssueCard(issue, i, 'keyboard'))}
                </div>
              </Section>
            )}

            {filteredIssues.imageAccessibilityIssues.length > 0 && (
              <Section title="Images" id="image-issues" icon={<ImageIcon className="w-5 h-5 text-blue-500" />} count={filteredIssues.imageAccessibilityIssues.length}>
                <div className="space-y-4">
                  {filteredIssues.imageAccessibilityIssues.map((issue, i) => renderIssueCard(issue, i, 'image'))}
                </div>
              </Section>
            )}

            {filteredIssues.formAccessibilityIssues.length > 0 && (
              <Section title="Forms" id="form-issues" icon={<FormInput className="w-5 h-5 text-indigo-500" />} count={filteredIssues.formAccessibilityIssues.length}>
                <div className="space-y-4">
                  {filteredIssues.formAccessibilityIssues.map((issue, i) => renderIssueCard(issue, i, 'form'))}
                </div>
              </Section>
            )}

            {filteredIssues.axeIssues.some(i => i.needsReview) && (
              <Section title="Needs Review" id="review-issues" icon={<ShieldCheck className="w-5 h-5 text-amber-500" />} count={filteredIssues.axeIssues.filter(i => i.needsReview).length}>
                <div className="space-y-4">
                  {filteredIssues.axeIssues.filter(i => i.needsReview).map((issue, i) => renderIssueCard(issue, i, 'axe'))}
                </div>
              </Section>
            )}
          </div>
        </div>

        {/* Right Col: Page Info & Meta */}
        <div className="space-y-6">
          <Section title="Page Metadata" id="meta" icon={<FileText className="w-5 h-5 text-blue-500" />}>
            <div className="space-y-4">
              <div>
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Language</span>
                <p className="font-medium text-gray-900">{result.pageInfo.language || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Viewport</span>
                <p className="font-medium text-gray-900">{result.pageInfo.viewport || 'Not specified'}</p>
              </div>
              <div className="pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Valid Doctype</span>
                  {result.pageInfo.hasValidDoctype ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Skip Links</span>
                  {result.pageInfo.hasSkipLinks ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                </div>
              </div>
            </div>
          </Section>

          {result.recommendations.length > 0 && (
            <Section title="Validation Steps" id="recommendations" icon={<List className="w-5 h-5 text-green-600" />}>
              <div className="space-y-3">
                {result.recommendations.map((rec, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold border border-green-200">{i + 1}</span>
                    <p className="text-sm text-gray-700">{rec}</p>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Lead Generation Section */}
          <div className="glass-card bg-white p-6 border-dashed border-2 border-primary-200">
            <div className="flex items-center text-primary-600 mb-4">
              <Heart className="w-5 h-5 mr-2" />
              <span className="font-bold tracking-wide uppercase text-xs">Accessibility Experts</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Need Help Fixing These?</h3>
            <p className="text-gray-600 text-sm mb-6">
              Don't spend hours debugging. Our experts can fix these accessibility issues for you and ensure your site is 100% compliant.
            </p>
            <a
              href="mailto:experts@a11y-scanner.com?subject=Accessibility Remediation Quote"
              className="block w-full text-center py-2.5 bg-primary-600 text-white font-bold rounded-lg shadow-lg hover:bg-primary-700 transition-colors"
            >
              Get a Free Quote
            </a>
          </div>

          {/* Privacy Disclaimer */}
          <div className="flex items-center justify-center space-x-2 py-4 px-6 bg-white/50 rounded-2xl border border-white/50 shadow-sm">
            <ShieldCheck className="w-4 h-4 text-primary-600" />
            <p className="text-xs text-gray-500 font-medium">
              We value your privacy. Your data is never stored outside of this active session.
            </p>
          </div>
        </div>
      </div>
    </div >
  );
};
